import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { cn } from '@shared/lib/cn';

/** Small section heading (title + optional action link) shown above a widget. */
export function WidgetHeader({
  title,
  actionLabel,
  onAction,
}: {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
}): JSX.Element {
  return (
    <div className="mb-2 flex items-center justify-between gap-3 px-0.5">
      <h2 className="text-body font-bold leading-tight text-ink">{title}</h2>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="flex shrink-0 items-center gap-0.5 text-body-sm font-semibold text-primary-700"
        >
          {actionLabel}
          <ChevronRight className="h-4 w-4" strokeWidth={2} />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Shared chrome for a home dashboard widget: a small section heading (title +
 * optional action link) *above* the card, with the widget body inside the card.
 */
export function WidgetCard({
  title,
  actionLabel,
  onAction,
  onClick,
  children,
  className,
}: {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Tapping the card body navigates to the widget's module. Interactive
   * children (buttons, hold-tiles) should call `e.stopPropagation()` so their
   * own taps don't also trigger this.
   */
  onClick?: () => void;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const tappable = !!onClick;
  return (
    <section>
      <WidgetHeader title={title} actionLabel={actionLabel} onAction={onAction} />
      <Card
        noGrain
        className={cn('rounded-xl p-4', tappable && 'cursor-pointer transition-transform active:scale-[0.99]', className)}
        {...(tappable
          ? {
              role: 'button',
              tabIndex: 0,
              onClick,
              onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              },
            }
          : {})}
      >
        {children}
      </Card>
    </section>
  );
}
